import type {
  MilestoneBody,
  MilestoneCandidatesResponse,
  MilestonesResponse,
  ProjectAcceptanceResponse,
  ProjectBoardResponse,
  ProjectDomainsResponse,
  ProjectOverviewResponse,
  ProjectsListResponse,
} from '@pulse/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

/** The list of projects, as of now with this week's figures (PRJ-7). */
export function useProjectsList() {
  return useQuery({
    queryKey: ['pages', 'projects'],
    queryFn: () => apiRequest<ProjectsListResponse>('/api/pages/projects'),
    staleTime: 60_000,
  });
}

/** A project's Overview (PRJ-8). */
export function useProjectOverview(productSlug: string) {
  return useQuery({
    queryKey: ['pages', 'projects', productSlug, 'overview'],
    queryFn: () => apiRequest<ProjectOverviewResponse>(`/api/pages/projects/${encodeURIComponent(productSlug)}`),
    staleTime: 60_000,
  });
}

/** A product's board for a business date (PRJ-1 to PRJ-4). */
export function useProjectBoard(productSlug: string, date: string, today: string) {
  return useQuery({
    queryKey: ['pages', 'projects', productSlug, 'board', date],
    queryFn: () =>
      apiRequest<ProjectBoardResponse>(`/api/pages/projects/${encodeURIComponent(productSlug)}/board`, {
        searchParams: { date: date === today ? undefined : date },
      }),
    staleTime: 60_000,
  });
}

/** A story repository's Domains tab, as of now (PRJ-5). */
export function useProjectDomains(productSlug: string) {
  return useQuery({
    queryKey: ['pages', 'projects', productSlug, 'domains'],
    queryFn: () => apiRequest<ProjectDomainsResponse>(`/api/pages/projects/${encodeURIComponent(productSlug)}/domains`),
    staleTime: 60_000,
  });
}

/** A story repository's Acceptance tab, as of now (PRJ-6). */
export function useProjectAcceptance(productSlug: string) {
  return useQuery({
    queryKey: ['pages', 'projects', productSlug, 'acceptance'],
    queryFn: () => apiRequest<ProjectAcceptanceResponse>(`/api/pages/projects/${encodeURIComponent(productSlug)}/acceptance`),
    staleTime: 60_000,
  });
}

/** A project's Milestones tab, as of now (MS-1 to MS-5). */
export function useMilestones(productSlug: string) {
  return useQuery({
    queryKey: ['milestones', productSlug],
    queryFn: () => apiRequest<MilestonesResponse>(`/api/milestones/${encodeURIComponent(productSlug)}`),
    staleTime: 60_000,
  });
}

/** The project's items matching a search, for adding to a milestone (MS-3). */
export function useMilestoneCandidates(productSlug: string, milestoneId: string | null, query: string) {
  return useQuery({
    queryKey: ['milestones', productSlug, milestoneId, 'candidates', query],
    queryFn: () =>
      apiRequest<MilestoneCandidatesResponse>(`/api/milestones/${encodeURIComponent(productSlug)}/${encodeURIComponent(milestoneId ?? '')}/candidates`, { searchParams: { q: query } }),
    enabled: milestoneId !== null,
    staleTime: 30_000,
  });
}

export type MilestoneChange =
  | { readonly kind: 'create'; readonly body: MilestoneBody }
  | { readonly kind: 'edit'; readonly milestoneId: string; readonly body: MilestoneBody }
  | { readonly kind: 'close' | 'reopen' | 'delete'; readonly milestoneId: string }
  | { readonly kind: 'add-item' | 'remove-item'; readonly milestoneId: string; readonly key: string };

/** Every change a member makes to a project's milestones (MS-2, MS-3). */
export function useChangeMilestones(productSlug: string) {
  const client = useQueryClient();
  const base = `/api/milestones/${encodeURIComponent(productSlug)}`;
  return useMutation({
    mutationFn: (change: MilestoneChange): Promise<unknown> => {
      switch (change.kind) {
        case 'create':
          return apiRequest(base, { method: 'POST', body: change.body });
        case 'edit':
          return apiRequest(`${base}/${encodeURIComponent(change.milestoneId)}`, { method: 'PUT', body: change.body });
        case 'close':
        case 'reopen':
          return apiRequest(`${base}/${encodeURIComponent(change.milestoneId)}/${change.kind}`, { method: 'POST' });
        case 'delete':
          return apiRequest(`${base}/${encodeURIComponent(change.milestoneId)}`, { method: 'DELETE' });
        case 'add-item':
          return apiRequest(`${base}/${encodeURIComponent(change.milestoneId)}/items`, { method: 'POST', body: { key: change.key } });
        case 'remove-item':
          return apiRequest(`${base}/${encodeURIComponent(change.milestoneId)}/items`, { method: 'DELETE', searchParams: { key: change.key } });
      }
    },
    // The project's tab and the Milestones page (MS-6) both show the change.
    onSuccess: () => client.invalidateQueries({ queryKey: ['milestones'] }),
  });
}
